import React, { useEffect, useState, useRef } from 'react';
import { manuscriptsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FileText, Clock, CheckCircle, XCircle, AlertCircle, Upload, Loader2, CalendarClock, Download, History } from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  'Recibido': { color: 'bg-blue-50 text-blue-600 border-blue-200', icon: Clock },
  'En Revision': { color: 'bg-amber-50 text-amber-600 border-amber-200', icon: Clock },
  'Revisiones Requeridas': { color: 'bg-orange-50 text-orange-600 border-orange-200', icon: AlertCircle },
  'Aceptado': { color: 'bg-green-50 text-green-600 border-green-200', icon: CheckCircle },
  'Rechazado': { color: 'bg-red-50 text-red-600 border-red-200', icon: XCircle },
  'Publicado': { color: 'bg-emerald-50 text-emerald-600 border-emerald-200', icon: CheckCircle },
};

export default function AuthorDashboard() {
  const { user } = useAuth();
  const [manuscripts, setManuscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resubmitDialog, setResubmitDialog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await manuscriptsAPI.list();
      const items = data.items || data;
      setManuscripts(items.filter(m => m.autor_id === user.id));
    } catch {}
    setLoading(false);
  };

  const handleResubmit = async () => {
    if (!resubmitDialog || !fileRef.current?.files?.[0]) return;
    setUploading(true);
    try {
      await manuscriptsAPI.resubmit(resubmitDialog.id, fileRef.current.files[0]);
      setResubmitDialog(null);
      toast.success('Manuscrito reenviado exitosamente');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reenviar');
    }
    setUploading(false);
  };

  const handleDownload = async (m) => {
    setDownloading(m.id);
    try {
      await manuscriptsAPI.downloadBlob(m.id);
      toast.success('Descarga iniciada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descargar.');
    }
    setDownloading(null);
  };

  if (loading) return <div className="flex-1 flex justify-center items-center"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>;

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8" data-testid="author-dashboard">
      <h1 className="text-2xl font-serif font-bold text-slate-dark mb-2">Mis Manuscritos</h1>
      <p className="text-sm font-sans text-gray-400 mb-6">{manuscripts.length} envio{manuscripts.length !== 1 ? 's' : ''}</p>

      {manuscripts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-sm border border-gray-100">
          <FileText className="w-10 h-10 text-gray-200 mx-auto mb-3" strokeWidth={1} />
          <p className="font-sans text-gray-400">Aun no has enviado manuscritos.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-sm divide-y divide-gray-50">
          {manuscripts.map((m, i) => {
            const sc = statusConfig[m.status] || statusConfig['En Revision'];
            const Icon = sc.icon;
            const canResubmit = ['Revisiones Requeridas', 'Rechazado'].includes(m.status);

            return (
              <div key={m.id} className="p-5 hover:bg-gray-50/30 transition-colors" data-testid={`author-ms-${i}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${sc.color.split(' ')[0]} border ${sc.color.split(' ').slice(2).join(' ')}`}>
                    <Icon className={`w-4 h-4 ${sc.color.split(' ')[1]}`} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Badge variant="outline" className={`text-[10px] font-sans mb-1 ${sc.color}`}>{m.status}</Badge>
                    <h3 className="font-serif font-semibold text-slate-dark leading-snug">{m.titulo}</h3>
                    <p className="text-xs font-sans text-gray-400 mt-1">
                      Enviado: {new Date(m.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {m.updated_at && m.updated_at !== m.created_at && <span> &middot; Actualizado: {new Date(m.updated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    </p>

                    {/* Deadline */}
                    {m.review_deadline && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-sans">
                        <CalendarClock className="w-3 h-3 text-gray-400" />
                        {(() => {
                          const dl = new Date(m.review_deadline);
                          const days = Math.ceil((dl - new Date()) / 86400000);
                          const ds = dl.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
                          if (days < 0) return <span className="text-red-500">Plazo vencido ({ds})</span>;
                          if (days <= 3) return <span className="text-amber-500">Vence en {days} dia{days !== 1 ? 's' : ''} ({ds})</span>;
                          return <span className="text-gray-500">Plazo: {ds}</span>;
                        })()}
                      </div>
                    )}

                    {/* Version history */}
                    {m.versions?.length > 0 && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-sans text-gray-400">
                        <History className="w-3 h-3" /> Version {m.versions.length + 1} &middot; {m.versions.length} reenvio{m.versions.length > 1 ? 's' : ''}
                      </div>
                    )}

                    {/* Latest review comments */}
                    {m.comments?.length > 0 && (
                      <div className="mt-2 p-2.5 bg-gray-50 rounded-sm border border-gray-100">
                        <p className="text-[10px] font-sans font-semibold text-gray-500 uppercase mb-1">Ultimo comentario</p>
                        <p className="text-xs font-sans text-gray-600">{m.comments[m.comments.length - 1].texto}</p>
                        <p className="text-[10px] font-sans text-gray-400 mt-1">— {m.comments[m.comments.length - 1].user_name}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-1.5 mt-3 sm:mt-0 sm:flex-col sm:shrink-0">
                    {m.file_available && (
                      <Button variant="outline" size="sm" className="font-sans text-xs h-8" disabled={downloading === m.id} onClick={() => handleDownload(m)} data-testid={`author-download-${i}`}>
                        {downloading === m.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />} Descargar
                      </Button>
                    )}
                    {canResubmit && (
                      <Button variant="outline" size="sm" className="font-sans text-xs h-8 text-orange-600 border-orange-200" onClick={() => setResubmitDialog(m)} data-testid={`resubmit-${i}`}>
                        <Upload className="w-3 h-3 mr-1" /> Subir Revision
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Resubmit dialog */}
      <Dialog open={!!resubmitDialog} onOpenChange={() => setResubmitDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Reenviar Manuscrito Revisado</DialogTitle></DialogHeader>
          {resubmitDialog && (
            <div className="space-y-4">
              <p className="text-sm font-sans text-gray-500">
                <strong>{resubmitDialog.titulo}</strong>
              </p>
              <p className="text-xs font-sans text-gray-400">
                Sube el archivo con los cambios solicitados. El estado cambiara a "En Revision" automaticamente.
              </p>
              <div>
                <label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Archivo revisado (PDF o Word)</label>
                <input type="file" ref={fileRef} accept=".pdf,.doc,.docx" className="text-sm font-sans w-full file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-sans file:bg-slate-dark file:text-white file:cursor-pointer" data-testid="resubmit-file-input" />
              </div>
              <Button onClick={handleResubmit} className="w-full bg-slate-dark font-sans" disabled={uploading} data-testid="confirm-resubmit-btn">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? 'Subiendo...' : 'Reenviar Manuscrito'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
